import { z } from 'zod';
import { isTenantModuleKey } from './tenant-modules.js';
/**
 * The tenant → Control event catalogue (v1).
 *
 * This is the SHARED definition of what the HospaSense tenant platform may tell
 * Control about a customer. It lives here, in `packages/contracts`, because the
 * two sides are separate deployments in separate repositories and the only
 * thing that keeps them honest is one written contract that both are checked
 * against. The tenant emitter mirrors these shapes; this file is what the
 * boundary actually enforces.
 *
 * THE RULE THAT SHAPES EVERY SCHEMA BELOW: Control must never ingest PHI, and a
 * hospital signup is not a patient record. So every schema here is `.strict()`
 * — an unexpected field is a rejection, not a silently-ignored extra — and the
 * fields are chosen to be the smallest set that answers the founder's actual
 * questions: who is this, how big are they, what did they ask for, when.
 *
 * WHAT IS DELIBERATELY ABSENT, and must stay absent:
 *  - No patient data of any kind, in any field.
 *  - No credentials. The tenant's SignupRequest row holds `hashedPassword` and
 *    `username`; neither appears here and neither may be added. The staff
 *    account that gets provisioned on approval is the tenant's business.
 *  - No staff names beyond ONE contact person, who is the human who filled the
 *    form in and expects to be contacted.
 *  - User counts are AGGREGATES. `user_count_changed` carries a number, never a
 *    roster.
 *  - No free-text prose fields. The tenant's intake captures "what frustrates
 *    you about your current system", which is genuinely useful to sales and is
 *    exactly the sort of field a hospital administrator might paste a clinical
 *    example into. It is not carried. If it is ever wanted, it needs its own
 *    decision, not an extra key on this schema.
 */
export const TENANT_EVENT_TYPES = [
    'organisation.signup_requested',
    'organisation.created',
    'organisation.trial_started',
    'organisation.trial_expiring',
    'organisation.trial_expired',
    'organisation.converted_to_paying',
    'organisation.suspended',
    'organisation.churned',
    'organisation.modules_changed',
    'organisation.user_count_changed',
];
/**
 * A tenant-side identifier, echoed back but never trusted as a Control key.
 *
 * Bounded and character-restricted rather than `z.string()`: these values are
 * written into Control's database and rendered in its UI, and the tenant is a
 * separate system whose ids we do not control. A cuid, a uuid and a legacy
 * numeric id all satisfy this; a 2 KB blob does not.
 */
const TenantId = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/, 'tenant identifier must be url-safe');
/**
 * The module keys a prospect ticked, validated against the tenant's catalogue.
 *
 * Closed vocabulary on purpose — see tenant-modules.ts for why, including the
 * measured interaction with the prohibited-content scanner.
 */
const TenantModules = z
    .array(z.string().trim().min(1).max(64))
    .max(120)
    .refine((keys) => keys.every(isTenantModuleKey), {
    message: 'requestedModules contains a key that is not in the tenant module catalogue',
});
/**
 * Deployment preference as the prospect answered it on the tenant's form.
 *
 * `unsure` is a real answer and the most common one — the tenant's own intake
 * says so — and it is carried rather than defaulted, because "we do not know
 * yet" and "they want managed cloud" lead to different first sales calls.
 */
export const DEPLOYMENT_PREFERENCES = [
    'managed_cloud',
    'customer_managed_cloud',
    'on_premises',
    'hybrid',
    'managed_on_premises',
    'unsure',
];
/**
 * How many people the practice said it has, in the tenant's own vocabulary.
 *
 * Carried because it decides how many logins the tenant provisions, so it is
 * the single best predictor of what the customer will see on day one — and a
 * `solo` answer against a 120-bed declared capacity is exactly the mismatch a
 * founder wants flagged on the pending-signup screen.
 */
export const STAFFING_SHAPES = ['solo', 'micro', 'small', 'full'];
export const SignupRequestedDataSchema = z
    .object({
    /** The tenant's SignupRequest id. Correlation only; never a Control key. */
    tenantSignupId: TenantId,
    organisationName: z.string().trim().min(1).max(200),
    organisationType: z.string().trim().min(1).max(64),
    deploymentPreference: z.enum(DEPLOYMENT_PREFERENCES),
    /** The one human who filled the form in and expects a reply. */
    contactName: z.string().trim().min(1).max(160),
    contactEmail: z.email().max(320),
    /**
     * Declared, not measured. These are what the prospect typed into a public
     * form, and the UI must present them as claims — UniSoft GmbH declared 120
     * beds and one member of staff, which is not a hospital, and the value of
     * the number is precisely that it can be wrong.
     */
    declaredBedCount: z.number().int().min(0).max(100_000).nullable(),
    declaredDailyOpdVolume: z.number().int().min(0).max(1_000_000).nullable(),
    requestedModules: TenantModules,
    staffingShape: z.enum(STAFFING_SHAPES).nullable(),
    /**
     * Country only — never the address, never the IP itself.
     *
     * The IP is personal data under DPDP and Control has no legitimate use for
     * it; the country is what tells a founder that a "hospital" declaring
     * Asia/Kolkata and INR actually signed up from Hesse. Two-letter ISO code.
     */
    sourceIpCountry: z
        .string()
        .regex(/^[A-Z]{2}$/, 'sourceIpCountry must be a two-letter uppercase ISO country code')
        .nullable(),
    requestedAt: z.iso.datetime(),
})
    .strict();
export const OrganisationCreatedDataSchema = z
    .object({
    tenantOrganisationId: TenantId,
    /** Present when the org came from an approved signup; null for seeded orgs. */
    tenantSignupId: TenantId.nullable(),
    organisationName: z.string().trim().min(1).max(200),
    deploymentType: z.enum(['cloud', 'on_prem']),
    /**
     * The tenant's OWN creation timestamp, not the delivery time.
     *
     * This is what makes a late backfill honest: Control records UniSoft GmbH
     * as created on 2026-09-07, which is when it was, rather than on the day
     * somebody finally wired the pipeline up.
     */
    createdAt: z.iso.datetime(),
})
    .strict();
const OrgRef = z.object({ tenantOrganisationId: TenantId });
export const TrialStartedDataSchema = OrgRef.extend({
    trialEndsAt: z.iso.datetime().nullable(),
    startedAt: z.iso.datetime(),
}).strict();
export const TrialExpiringDataSchema = OrgRef.extend({
    trialEndsAt: z.iso.datetime(),
    daysRemaining: z.number().int().min(0).max(365),
}).strict();
export const TrialExpiredDataSchema = OrgRef.extend({
    expiredAt: z.iso.datetime(),
}).strict();
export const ConvertedToPayingDataSchema = OrgRef.extend({
    effectiveAt: z.iso.datetime(),
    /**
     * Which of the two writers moved this customer.
     *
     * `subscriptionStatus` has two legitimate paths to `active`: a paid Stripe
     * invoice, and a signed `set_commercial_status` command from Control for the
     * hospitals that sign a contract and pay by NEFT sixty days later. Knowing
     * which fired is the difference between "the card cleared" and "we decided".
     */
    source: z.enum(['stripe', 'control_command', 'manual']),
}).strict();
export const SuspendedDataSchema = OrgRef.extend({
    reason: z.string().trim().min(1).max(500),
    suspendedAt: z.iso.datetime(),
}).strict();
export const ChurnedDataSchema = OrgRef.extend({
    reason: z.string().trim().min(1).max(500),
    churnedAt: z.iso.datetime(),
}).strict();
export const ModulesChangedDataSchema = OrgRef.extend({
    /** The full enabled set after the change, not a delta — deltas drift. */
    enabledModules: TenantModules,
    changedAt: z.iso.datetime(),
}).strict();
export const UserCountChangedDataSchema = OrgRef.extend({
    /** Aggregate only. Never a roster, never a name, never an email. */
    activeUserCount: z.number().int().min(0).max(1_000_000),
    changedAt: z.iso.datetime(),
}).strict();
export const TENANT_EVENT_DATA_SCHEMAS = {
    'organisation.signup_requested': SignupRequestedDataSchema,
    'organisation.created': OrganisationCreatedDataSchema,
    'organisation.trial_started': TrialStartedDataSchema,
    'organisation.trial_expiring': TrialExpiringDataSchema,
    'organisation.trial_expired': TrialExpiredDataSchema,
    'organisation.converted_to_paying': ConvertedToPayingDataSchema,
    'organisation.suspended': SuspendedDataSchema,
    'organisation.churned': ChurnedDataSchema,
    'organisation.modules_changed': ModulesChangedDataSchema,
    'organisation.user_count_changed': UserCountChangedDataSchema,
};
/**
 * The lifecycle state a tenant event implies, where it implies one.
 *
 * Control's lifecycle machine is its own (LIFECYCLE_TRANSITIONS in
 * lifecycle.ts) and the tenant does not get to drive it directly: an event
 * arriving out of order, or for a customer an operator has already churned,
 * must not rewrite Control's state. This map is therefore a PROPOSAL, applied
 * only where `canTransition` agrees; the processor records the event either
 * way, so the history is complete even when the state does not move.
 *
 * `signup_requested` is absent on purpose — a pending signup is not yet an
 * organisation and has no lifecycle state to be in.
 */
export const TENANT_EVENT_LIFECYCLE_INTENT = {
    'organisation.trial_started': 'evaluation',
    'organisation.converted_to_paying': 'live',
    'organisation.suspended': 'suspended',
    'organisation.churned': 'churned',
};
//# sourceMappingURL=tenant-events.js.map